import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Form,
  message,
  Select,
  InputNumber,
  Spin,
  Table,
  Button,
  Space,
  Checkbox,
  Empty,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates, EXCHANGE_RATES_UPDATED_EVENT } from "../../../shared/lib/exchangeRates";
import {
  useAccountantsCatalog,
  useCustomersCatalog,
  useProductsCatalog,
} from "../../../hooks/useCatalogData";
import "../../../shared/styles/ErpPages.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const EditOrder = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [accountant, setAccountant] = useState(null);
  const [amountPaid, setAmountPaid] = useState(0);
  const [amountPaidMain, setAmountPaidMain] = useState(0);
  const [amountPaidSecondary, setAmountPaidSecondary] = useState(0);
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [exchangeRates, setExchangeRates] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [allowAdjustments, setAllowAdjustments] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentSender] = useState("");
  const [paymentReceiver] = useState("");
  const [newProductId, setNewProductId] = useState(null);
  const [newProductQty, setNewProductQty] = useState(1);

  const { data: accountants } = useAccountantsCatalog();
  const { data: customerOptions } = useCustomersCatalog();
  const { data: storeProducts } = useProductsCatalog();

  const mainCode = systemConfig?.currency_code || "IQD";
  const secondaryCode = systemConfig?.secondary_currency_code || "";
  const useDualCurrency = Boolean(secondaryCode);

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedMainTotal = useMemo(() => {
    const main = toApiNumber(amountPaidMain, 0);
    const sec = toApiNumber(amountPaidSecondary, 0);
    if (!useDualCurrency) return toApiNumber(amountPaid, 0);
    const secondaryPerMain = selectedRate ? toApiNumber(selectedRate.main_per_secondary, 0) : 0;
    if (!secondaryPerMain) return Math.round(main * 100) / 100;
    return Math.round((main + sec / secondaryPerMain) * 100) / 100;
  }, [amountPaid, amountPaidMain, amountPaidSecondary, selectedRate, useDualCurrency]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await apiClient.get(`/orders/${id}`);
        if (cancelled) return;
        const res = { ...response.data };
        const toNumber = (v) => {
          if (v === null || v === undefined) return 0;
          if (typeof v === "number") return v;
          try {
            return parseFloat(String(v).replace(/,/g, "")) || 0;
          } catch {
            return 0;
          }
        };

        res.amount_paid = toNumber(res.amount_paid);
        res.total_price = toNumber(res.total_price);

        const normalizedProducts = (res.order_products || []).map((item) => {
          const normalized = { ...item };
          normalized.quantity = parseInt(normalized.quantity, 10) || 0;
          normalized.single_price = toNumber(normalized.single_price);
          normalized.sub_qty = toNumber(normalized.sub_qty) || null;
          normalized.sub_price = toNumber(normalized.sub_price) || null;
          return normalized;
        });

        res.order_products = normalizedProducts;
        setCart(
          normalizedProducts.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
            single_price: item.single_price,
            sub_qty: item.sub_qty,
            sub_price: item.sub_price,
            product: item.product,
          }))
        );
        setCustomer(res.customer?.id || null);
        setAccountant(res.accountant || null);
        setAmountPaid(res.amount_paid || 0);
        if (res.amount_paid_main != null || res.amount_paid_secondary != null) {
          setAmountPaidMain(toApiNumber(res.amount_paid_main, res.amount_paid || 0));
          setAmountPaidSecondary(toApiNumber(res.amount_paid_secondary, 0));
        } else {
          setAmountPaidMain(toApiNumber(res.amount_paid, 0));
          setAmountPaidSecondary(0);
        }
        setPaymentMethod(response.data.payment_method || "cash");

        const cfg = await fetchSystemConfig();
        if (cancelled) return;
        setSystemConfig(cfg);
        if (cfg?.secondary_currency_code) {
          const rates = await fetchExchangeRates();
          if (cancelled) return;
          setExchangeRates(rates);
          const preferred = res.exchange_rate_id;
          const rid =
            preferred && rates.some((r) => Number(r.id) === Number(preferred))
              ? Number(preferred)
              : rates[0]?.id ?? null;
          setExchangeRateId(rid);
        } else {
          setExchangeRates([]);
          setExchangeRateId(null);
        }

        setPaymentsLoading(true);
        try {
          const pr = await apiClient.get(`/orders/${id}/payments`);
          if (!cancelled) setPayments(pr.data || []);
        } catch {
          if (!cancelled) setPayments([]);
        } finally {
          if (!cancelled) setPaymentsLoading(false);
        }
      } catch {
        message.error(t("editOrderLoadFailed", "Failed to load order for editing"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  useEffect(() => {
    const onFxUpdated = () => {
      (async () => {
        try {
          const cfg = await fetchSystemConfig();
          setSystemConfig(cfg);
          if (!cfg?.secondary_currency_code) {
            setExchangeRates([]);
            setExchangeRateId(null);
            return;
          }
          const rates = await fetchExchangeRates();
          setExchangeRates(rates);
          setExchangeRateId((prev) =>
            prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
          );
        } catch {
          /* ignore */
        }
      })();
    };
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
  }, []);

  const getTranslatedName = useCallback(
    (nameInput) => {
      let nameTranslations;
      try {
        nameTranslations = typeof nameInput === "string" ? JSON.parse(nameInput) : nameInput;
      } catch {
        return t("unnamedProduct", "Unnamed Product");
      }
      return nameTranslations?.[i18n.language] || nameTranslations?.en || t("nA", "N/A");
    },
    [i18n.language, t]
  );

  const getItemSubtotal = (item) => {
    const subtotal =
      item.editedSubtotal !== undefined ? item.editedSubtotal : item.quantity * item.single_price;
    return Math.round(subtotal * 100) / 100;
  };

  const handlePriceChange = (index, newPrice) => {
    setCart((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const price = parseFloat(newPrice) || 0;
        const qty = item.quantity;
        const subQty = item.sub_qty || 0;
        const subtotal = price * qty;
        const subUnitRatio = item.product?.sub_unit_ratio || 1;
        const subPrice = subUnitRatio > 0 ? price / subUnitRatio : 0;
        return {
          ...item,
          single_price: price,
          editedSubtotal: subtotal,
          sub_price: subQty > 0 ? subPrice : item.sub_price,
        };
      })
    );
  };

  const handleCartChange = (index, field, value) => {
    setCart((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };

        if (field === "quantity") {
          const newQty = value;
          const price = item.single_price;
          updated.editedSubtotal = price * newQty;
          if (item.sub_qty && item.product?.sub_unit_ratio) {
            updated.sub_qty = newQty * item.product.sub_unit_ratio;
          }
          if (updated.sub_qty > 0) {
            updated.sub_price = updated.editedSubtotal / updated.sub_qty;
          }
        } else if (field === "sub_qty") {
          const subtotal =
            item.editedSubtotal !== undefined
              ? item.editedSubtotal
              : item.quantity * item.single_price;
          if (value > 0) updated.sub_price = subtotal / value;
        }

        return updated;
      })
    );
  };

  const removeCartItem = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const addCartItem = () => {
    if (!newProductId) return message.error(t("editOrderSelectProductRequired", "Please select a product to add"));
    const product = storeProducts.find((p) => p.id === newProductId);
    if (!product) return message.error(t("editOrderProductNotFound", "Selected product not found"));
    setCart((prev) => [
      ...prev,
      {
        product_id: product.id,
        quantity: newProductQty || 1,
        single_price: parseFloat(product.price || 0),
        sub_qty: product.sub_unit_ratio ? newProductQty * (product.sub_unit_ratio || 1) : null,
        sub_price: product.sub_unit_price || null,
        product,
      },
    ]);
    setNewProductId(null);
    setNewProductQty(1);
  };

  const cartSubtotal = useMemo(() => {
    let total = 0;
    cart.forEach((item) => {
      total += getItemSubtotal(item);
    });
    return Math.round(total * 100) / 100;
  }, [cart]);

  const paymentsTotal = useMemo(
    () => payments.reduce((s, p) => s + Number(p.amount || 0), 0),
    [payments]
  );

  const handleSubmit = async () => {
    const toMoney = (value) => Math.round(toApiNumber(value, 0) * 100) / 100;
    const dual = Boolean(systemConfig?.secondary_currency_code);
    const secVal = toMoney(amountPaidSecondary);
    if (dual && secVal > 0 && !exchangeRateId) {
      message.error(t("editOrderSelectExchangeRate", "Please select an exchange rate."));
      return;
    }

    const orderJson = {
      customer_id: customer,
      accountant,
      payment_method: paymentMethod,
      sender: paymentSender || null,
      receiver: paymentReceiver || null,
      order_items: cart.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        single_price: item.single_price,
        sub_qty: item.sub_qty,
        sub_price: item.sub_price,
      })),
    };
    if (dual) {
      orderJson.amount_paid_main = toMoney(amountPaidMain);
      orderJson.amount_paid_secondary = secVal;
      if (secVal > 0) orderJson.exchange_rate_id = exchangeRateId;
    } else {
      orderJson.amount_paid = toMoney(amountPaid);
    }

    try {
      if (payments.length > 0 && !allowAdjustments) {
        message.error(
          t("editOrderPaymentsBlock", "This order has payments. Enable adjustments to proceed or contact accounting.")
        );
        return;
      }
      if (payments.length > 0 && allowAdjustments) {
        orderJson.create_adjustment = true;
      }
      setSaving(true);
      await apiClient.put(`/orders/${id}`, orderJson);
      message.success(t("editOrderUpdateSuccess", "Order updated successfully"));
      navigate("/TableOrder");
    } catch (error) {
      const msg = error?.response?.data?.message;
      message.error(msg || t("editOrderUpdateFailed", "Failed to update order"));
    } finally {
      setSaving(false);
    }
  };

  const lineColumns = [
    {
      title: t("product", "Product"),
      key: "product",
      render: (_, item) => getTranslatedName(item.product?.name) || item.product_id,
    },
    {
      title: t("quantity", "Quantity"),
      key: "quantity",
      width: 110,
      render: (_, item, idx) => (
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          value={item.quantity}
          onChange={(v) => handleCartChange(idx, "quantity", parseInt(v, 10) || 0)}
        />
      ),
    },
    {
      title: t("singlePrice", "Single price"),
      key: "single_price",
      width: 130,
      render: (_, item, idx) => (
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          value={Math.round(item.single_price * 100) / 100}
          onChange={(v) => handlePriceChange(idx, v)}
        />
      ),
    },
    {
      title: t("subQty", "Sub qty"),
      key: "sub_qty",
      width: 110,
      render: (_, item, idx) => (
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          value={item.sub_qty || null}
          onChange={(v) => handleCartChange(idx, "sub_qty", parseFloat(v) || 0)}
        />
      ),
    },
    {
      title: t("subPrice", "Sub price"),
      key: "sub_price",
      width: 110,
      render: (_, item) =>
        item.sub_price != null ? formatCurrency(Math.round(item.sub_price * 100) / 100, mainCode) : "—",
    },
    {
      title: t("subtotal", "Subtotal"),
      key: "subtotal",
      width: 120,
      align: "right",
      render: (_, item) => (
        <span className="erp-money">{formatCurrency(getItemSubtotal(item), mainCode)}</span>
      ),
    },
    {
      title: t("action", "Action"),
      key: "action",
      width: 100,
      fixed: "right",
      render: (_, __, idx) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeCartItem(idx)}>
          {t("remove", "Remove")}
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="erp-page" style={{ textAlign: "center", paddingTop: 80 }}>
        <Spin size="large" tip={t("editOrderLoading", "Loading order...")} />
      </div>
    );
  }

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              <Button
                type="link"
                size="small"
                icon={<ArrowLeftOutlined />}
                style={{ padding: 0 }}
                onClick={() => navigate("/TableOrder")}
              >
                {t("orders", "Orders")}
              </Button>
              {" / "}
              {t("editOrder", "Edit order")} #{id}
            </div>
            <h1>
              {t("editOrder", "Edit order")} #{id}
            </h1>
          </div>
          <Space>
            <Button onClick={() => navigate("/TableOrder")}>{t("cancel", "Cancel")}</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
              {t("saveChanges", "Save changes")}
            </Button>
          </Space>
        </div>

        <div className="erp-meta">
          <span>
            {t("cartSubtotal", "Cart subtotal")}:{" "}
            <b className="erp-money">{formatCurrency(cartSubtotal, mainCode)}</b>
          </span>
          <span>
            {t("lines", "Lines")}: <b>{cart.length}</b>
          </span>
          {payments.length > 0 ? (
            <span>
              {t("totalPaid", "Total paid")}:{" "}
              <b className="erp-money ok">{formatCurrency(paymentsTotal, mainCode)}</b>
            </span>
          ) : null}
        </div>

        <div className="erp-toolbar" style={{ flexWrap: "wrap" }}>
          <Select
            showSearch
            optionFilterProp="label"
            value={newProductId}
            onChange={setNewProductId}
            style={{ minWidth: 260, flex: 1, maxWidth: 420 }}
            placeholder={t("editOrderSelectProduct", "Select product to add")}
            options={(storeProducts || []).map((p) => ({
              value: p.id,
              label: `${getTranslatedName(p.name)} — ${formatCurrency(p.price || 0, mainCode)}`,
            }))}
          />
          <InputNumber
            min={1}
            value={newProductQty}
            onChange={(v) => setNewProductQty(v || 1)}
            style={{ width: 100 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={addCartItem}>
            {t("addProduct", "Add product")}
          </Button>
        </div>

        <div className="erp-body">
          <Table
            size="small"
            rowKey={(r) => `${r.product_id}-${r.quantity}-${r.single_price}`}
            dataSource={cart}
            columns={lineColumns}
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: t("noDataFound", "No records") }}
          />
        </div>

        <div className="erp-body" style={{ padding: 14, borderTop: "1px solid #e2e8f0" }}>
          <div className="erp-settings-pane" style={{ maxWidth: 720 }}>
            <h2 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>
              {t("orderDetails", "Order details")}
            </h2>
            <Form form={form} layout="vertical">
              <Form.Item label={t("selectCustomer", "Select customer")} required>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("selectACustomer", "Select a customer")}
                  value={customer}
                  onChange={setCustomer}
                  options={(customerOptions || []).map((cust) => ({
                    value: cust.id,
                    label: cust.name,
                  }))}
                />
              </Form.Item>
              <Form.Item label={t("selectAccountant", "Select accountant")} required>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("selectAnAccountant", "Select an accountant")}
                  value={accountant}
                  onChange={setAccountant}
                  options={(accountants || []).map((acc) => ({
                    value: acc.id,
                    label: acc.name,
                  }))}
                />
              </Form.Item>

              {useDualCurrency ? (
                <>
                  <Form.Item label={`${t("amountPaid", "Amount paid")} (${mainCode})`}>
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      value={amountPaidMain}
                      onChange={(v) => setAmountPaidMain(v ?? 0)}
                    />
                  </Form.Item>
                  <Form.Item label={`${t("amountPaid", "Amount paid")} (${secondaryCode})`}>
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      value={amountPaidSecondary}
                      onChange={(v) => setAmountPaidSecondary(v ?? 0)}
                    />
                  </Form.Item>
                  {exchangeRates.length > 0 ? (
                    <Form.Item label={t("exchangeRate", "Exchange rate")}>
                      <Select
                        value={exchangeRateId}
                        onChange={setExchangeRateId}
                        options={exchangeRates.map((r) => ({
                          value: r.id,
                          label: `${String(r.effective_from || "").slice(0, 10)} — 1 ${mainCode} = ${r.main_per_secondary} ${secondaryCode}`,
                        }))}
                      />
                    </Form.Item>
                  ) : null}
                  <div className="erp-meta" style={{ border: "1px solid #e2e8f0", marginBottom: 12 }}>
                    <span>
                      {t("totalApplied", "Total applied")} ({mainCode}):{" "}
                      <b>{formatCurrency(appliedMainTotal, mainCode)}</b>
                    </span>
                  </div>
                </>
              ) : (
                <Form.Item label={t("amountPaid", "Amount paid")} required>
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    value={amountPaid}
                    onChange={(v) => setAmountPaid(v ?? 0)}
                  />
                </Form.Item>
              )}
            </Form>

            <h2 style={{ margin: "16px 0 12px", fontSize: 14, fontWeight: 700 }}>
              {t("payments", "Payments")}
            </h2>
            {paymentsLoading ? (
              <Spin />
            ) : payments.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("editOrderNoPayments", "No payments recorded for this order.")} />
            ) : (
              <>
                <div className="erp-meta" style={{ border: "1px solid #e2e8f0", marginBottom: 8 }}>
                  <span>
                    {t("totalPaid", "Total paid")}:{" "}
                    <b className="erp-money ok">{formatCurrency(paymentsTotal, mainCode)}</b>
                  </span>
                </div>
                <Table
                  size="small"
                  rowKey={(r, i) => `${r.id || i}-${r.created_at}`}
                  dataSource={payments}
                  pagination={false}
                  scroll={{ x: "max-content" }}
                  columns={[
                    {
                      title: t("date", "Date"),
                      dataIndex: "created_at",
                      width: 160,
                      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"),
                    },
                    {
                      title: t("amount", "Amount"),
                      dataIndex: "amount",
                      align: "right",
                      render: (v) => formatCurrency(v, mainCode),
                    },
                    {
                      title: t("note", "Note"),
                      dataIndex: "note",
                      render: (v) => v || "—",
                    },
                  ]}
                />
                <div style={{ marginTop: 12 }}>
                  <Checkbox checked={allowAdjustments} onChange={(e) => setAllowAdjustments(e.target.checked)}>
                    {t("editOrderAllowAdjustments", "Allow adjustments to this order (will create adjustment records)")}
                  </Checkbox>
                </div>
              </>
            )}

            <Space style={{ marginTop: 20 }}>
              <Link to={`/InfoOrder/${id}`}>
                <Button>{t("viewInvoice", "View invoice")}</Button>
              </Link>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
                {t("saveChanges", "Save changes")}
              </Button>
            </Space>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOrder;
